import { z } from 'zod'
import {
  SignatureSchema,
  SignaturePageSchema,
  SignatureMatchSchema,
  SigningPositionSchema,
  UpdateSignatureCoordinatesSchema,
} from '../schemas'
import { http } from './http'
import { auth } from './auth'
import { parseWithZod } from '../utils/parse-with-zod'
import type { SignaturePage, SigningPosition, UpdateSignatureCoordinates } from '../types'

const SignatureResponseSchema = z.object({ signature: SignatureSchema })
const CompleteSignatureMatchResponseSchema = z.object({ signatures: z.array(SignatureSchema) })
const SignaturePageResponseSchema = z.object({ signature_page: SignaturePageSchema })
const SignaturePageTextResponseSchema = z.object({
  text: z.string(),
  status: z.enum(['generating', 'unavailable', 'ready']),
})
const SigningPositionResponseSchema = z.object({ signing_position: SigningPositionSchema })
const FetchSigningPackageUrlResponseSchema = z.object({ package_url: z.string().nullable() })

export const signatures = {
  // ---- Signature pages ----------------------------------------

  /**
   * Creates the signature page for a document page that has none. In the signature editor a page
   * only becomes a signature page once a signatory is added to it, so this is called lazily on the
   * first save rather than up front.
   */
  createSignaturePage: async (
    payload: { item_id: number; source_version_id: number; source_page_number: number },
    dealId: number,
  ) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/signature_pages`,
      { signature_page: payload },
      dealId,
    )
    return parseWithZod(SignaturePageResponseSchema, result).signature_page
  },

  getSignaturePage: async (id: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/signature_pages/${id}`, undefined, dealId)
    return parseWithZod(SignaturePageResponseSchema, result).signature_page
  },

  updateSignaturePage: async (id: number, payload: SignaturePage, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.put(
      `${regionHost}/api/signature_pages/${id}`,
      { signature_page: payload },
      dealId,
    )
    return parseWithZod(SignaturePageResponseSchema, result).signature_page
  },

  deleteSignaturePage: async (id: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    await http.delete(`${regionHost}/api/signature_pages/${id}`, dealId)
  },

  clearDetectedSignatures: async (versionId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    await http.post(
      `${regionHost}/api/versions/${versionId}/clear_detected_signatures`,
      undefined,
      dealId,
    )
  },

  clearSignaturePageStatus: async (id: number, status: 'obsolete' | 'altered', dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/signature_pages/${id}/clear_signatures_status`,
      { status },
      dealId,
    )
    return parseWithZod(z.object({ signatures: z.array(SignatureSchema) }), result).signatures
  },

  getSignaturePageText: async (id: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/signature_pages/${id}/text`, undefined, dealId)
    return parseWithZod(SignaturePageTextResponseSchema, result)
  },

  // ---- Signatures ---------------------------------------------

  createSignature: async (
    payload: { item_id: number; signing_position_id: number; signature_page_id: number },
    dealId: number,
  ) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(`${regionHost}/api/signatures`, { signature: payload }, dealId)
    return parseWithZod(SignatureResponseSchema, result).signature
  },

  deleteSignature: async (id: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    await http.delete(`${regionHost}/api/signatures/${id}`, dealId)
  },

  updateCoordinates: async (id: number, payload: UpdateSignatureCoordinates, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/signatures/${id}/update_coordinates`,
      parseWithZod(UpdateSignatureCoordinatesSchema, payload),
      dealId,
    )
    return parseWithZod(SignatureResponseSchema, result).signature
  },

  clearExecutedFile: async (id: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/signatures/${id}/clear_executed_file`,
      undefined,
      dealId,
    )
    return parseWithZod(SignatureResponseSchema, result).signature
  },

  markExecuted: async (signatureIds: number[], dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/signatures/mark_executed`,
      { signature_ids: signatureIds },
      dealId,
    )
    return parseWithZod(z.object({ signatures: z.array(SignatureSchema) }), result).signatures
  },

  // ---- Signing positions --------------------------------------

  createSigningPosition: async (
    payload: Omit<SigningPosition, 'id' | 'signature_ids'>,
    dealId: number,
  ) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/signing_positions`,
      { signing_position: payload },
      dealId,
    )
    return parseWithZod(SigningPositionResponseSchema, result).signing_position
  },

  updateSigningPosition: async (
    id: number,
    payload: Omit<SigningPosition, 'id' | 'signature_ids'>,
    dealId: number,
  ) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.put(
      `${regionHost}/api/signing_positions/${id}`,
      { signing_position: payload },
      dealId,
    )
    return parseWithZod(SigningPositionResponseSchema, result).signing_position
  },

  deleteSigningPosition: async (id: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    await http.delete(`${regionHost}/api/signing_positions/${id}`, dealId)
  },

  detectExecutedSignatures: async (
    dealId: number,
    payload: { upload_id: number; page_numbers: number[] },
  ) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deals/${dealId}/detect_executed_signatures`,
      payload,
      dealId,
    )
    return parseWithZod(z.object({ signature_matches: z.array(SignatureMatchSchema) }), result)
      .signature_matches
  },

  getSignatureMatch: async (id: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/signature_matches/${id}`, undefined, dealId)
    return parseWithZod(z.object({ signature_match: SignatureMatchSchema }), result).signature_match
  },

  getSignatureMatches: async (dealId: number, ids: number[]) => {
    const regionHost = auth.getUserRegionHost()
    const searchParams = new URLSearchParams()
    ids.forEach(id => searchParams.append('ids[]', String(id)))
    const result = await http.get(
      `${regionHost}/api/deals/${dealId}/signature_matches?${searchParams}`,
      undefined,
      dealId,
    )
    return parseWithZod(z.object({ signature_matches: z.array(SignatureMatchSchema) }), result)
      .signature_matches
  },

  completeSignatureMatch: async (id: number, signatureIds: number[], dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/signature_matches/${id}/complete`,
      { signature_ids: signatureIds },
      dealId,
    )
    return parseWithZod(CompleteSignatureMatchResponseSchema, result).signatures
  },

  // ---- Signing packages --------------------------------------

  fetchSigningPackageUrl: async (packageId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/signing_package_to_individuals/${packageId}/package_url`,
      undefined,
      dealId,
    )
    return parseWithZod(FetchSigningPackageUrlResponseSchema, result)
  },
}
